import { T } from '../data/tokens';
import FlatIcon from './FlatIcon';
import Gallery from './Gallery';
import Accordion from './Accordion';

const accent  = '#00eb9c';
const accentR = '0,235,156';

const SCREENS = [
  { label: 'Login Screen',      src: 'images/we-connect-login.png',     bg: '#F0FDF4', fg: '#15803d' },
  { label: 'Flow Builder- A',      src: 'images/visual-flow-builder-a.png',     bg: '#F0FDF4', fg: '#15803d' },
  { label: 'Flow Builder- b',  src: 'images/visual-flow-builder-b.png', bg: '#ECFDF5', fg: '#059669' },
  { label: 'Flow Builder- c',         src: 'images/visual-flow-builder-c.png',        bg: '#F0FDFA', fg: '#0d9488' },
  { label: 'Flow Builder Wireframe Design',       src: 'images/visual-flow-builder-wireframe-design.png',      bg: '#F0FFF4', fg: '#16a34a' },
  { label: 'WeConnect Information Architecture and Flow',       src: 'images/we-information-architecture-flow.png',      bg: '#F0FFF4', fg: '#16a34a' },
];
 
const IMPACT_GROUPS = [
  { groupLabel: 'Activation & Growth', groupIcon: 'rocket', stats: [
    { num: '78%',   desc: 'Activation Rate',           sub: 'Highest in company history' },
    { num: '2,450', desc: 'Businesses Onboarded',      sub: 'Within 3 months' },
    { num: '3x',    desc: 'Activation Improvement',    sub: '22% to 78%' },
  ]},
  { groupLabel: 'Speed & Usability', groupIcon: 'bolt', stats: [
    { num: '28min', desc: 'Avg. Bot Creation Time',    sub: 'Down from 3 days' },
    { num: '84',    desc: 'SUS Score',                 sub: 'Up from 61' },
    { num: '62%',   desc: 'Support Tickets Reduced',   sub: 'Post-launch' },
  ]},
  { groupLabel: 'Business Outcomes', groupIcon: 'work', stats: [
    { num: '41%',   desc: 'Increase in Lead Capture',  sub: 'For active users' },
    { num: '3.8x',  desc: 'Bot Engagement',            sub: 'Avg. first 90 days' },
    { num: '89%',   desc: 'Retention Rate',            sub: 'Among activated users' },
  ]},
];

export default function CaseStudyConv() {
  return (
    <div>

      {/* ── Hero ────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(135deg, #075E66 0%, #0a8a6a 50%, #00eb9c 100%)', color: 'white', padding: '3.5rem 3rem 3rem', position: 'relative', overflow: 'hidden' }} className="cs-hero">
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(rgba(${accentR},0.08) 1px,transparent 1px),linear-gradient(90deg,rgba(${accentR},0.08) 1px,transparent 1px)`, backgroundSize: '48px 48px', pointerEvents: 'none' }} />
        <div style={T.heroInner}>
          <span style={{ ...T.pill, background: `rgba(${accentR},0.2)`, border: `1px solid rgba(${accentR},0.4)`, color: '#e0fff5' }}>AI · SaaS · Conversational Marketing</span>
          <h2 style={T.heroTitle}>Conversational Marketing Platform</h2>
          <p style={T.heroSub}>Weconnect — Empowering SMBs with AI-Powered Engagement</p>
          <p style={{ ...T.heroTagline, color: accent }}>78% Activation Rate &amp; 2,450 Businesses Onboarded in 3 Months</p>
          <div style={{ ...T.resultBand, borderLeft: '4px solid #00eb9c' }}>
            <p style={T.resultText}>
              I led the end-to-end product design for a <strong>no-code AI chatbot platform</strong> targeting non-technical SMB owners. By redesigning the onboarding funnel three times based on activation data, we achieved the <strong>highest activation rate in company history</strong> within 30 days of launch.
            </p>
          </div>
          <div style={T.metricsRow} className="cs-metrics">
            <div style={T.metric}><span style={T.metricNum}>78%</span><span style={T.metricLabel}>Activation Rate</span><span style={T.metricSub}>Highest in company history</span></div>
            <div style={T.metric}><span style={T.metricNum}>2,450</span><span style={T.metricLabel}>Businesses Onboarded</span><span style={T.metricSub}>Within 3 months</span></div>
            <div style={T.metric}><span style={T.metricNum}>30min</span><span style={T.metricLabel}>Bot Launch Time</span><span style={T.metricSub}>No coding required</span></div>
            <div style={T.metric}><span style={T.metricNum}>3x</span><span style={T.metricLabel}>Onboarding Redesigns</span><span style={T.metricSub}>Data-driven iterations</span></div>
          </div>
        </div>
      </div>

      {/* ── Stats Strip ─────────────────────────────────────────────── */}
      <div style={T.detailStatsStrip} className="detail-stats-strip">
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Role</span><span style={T.detailStatCellValue}>Product Designer</span></div>
        <div style={{ ...T.detailStatCell, borderRight: '1px solid rgba(0,0,0,0.07)' }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Duration</span><span style={T.detailStatCellValue}>15 Months</span></div>
        <div style={{ ...T.detailStatCell }} className="detail-stat-cell"><span style={T.detailStatCellLabel}>Team</span><span style={T.detailStatCellValue}>4-Person Product Team</span></div>
      </div>

      {/* ── Problem ─────────────────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="help" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Problem</p>
        <h3 style={T.sectionTitle}>SMBs couldn&#39;t build bots without a developer</h3>
        <p style={T.sectionIntro}>Weconnect had the AI technology but only 22% of sign-ups ever launched a live chatbot. Non-technical SMB owners faced a blank canvas with no guidance, generic templates that felt irrelevant, and a complex flow builder that required coding knowledge. Every week of low activation was costing the business growth.</p>
        <div style={{ display: 'flex', gap: '12px', marginTop: '1.25rem' }}>
          {[
            { v: '22%',    l: 'activation rate at launch' },
            { v: '3 days', l: 'avg. bot creation time' },
            { v: '61',     l: 'NPS score pre-redesign' },
          ].map((s) => (
            <div key={s.v} style={{ flex: 1, borderRadius: '10px', padding: '1rem 1.25rem', background: `rgba(${accentR},0.06)`, border: `1px solid rgba(${accentR},0.2)` }}>
              <div style={{ fontFamily: "'DM Serif Display',Georgia,serif", fontSize: '1.7rem', color: accent, lineHeight: 1, marginBottom: '4px' }}>{s.v}</div>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', color: '#64748b', letterSpacing: '0.04em' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Solution — 5 cards ──────────────────────────────────────── */}
      <div style={T.section} className="cs-section">
        <p style={{ ...T.sectionLabel, color: accent }}><FlatIcon name="rocket" size={14} color={accent} style={{marginRight:"6px",verticalAlign:"middle"}} />The Final Solution</p>
        <h3 style={T.sectionTitle}>What We Built</h3>
        <p style={T.sectionIntro}>A no-code platform that lets any SMB owner build, launch, and optimize an AI-powered chatbot in under 30 minutes — no technical background required.</p>
        <div style={T.solCards}>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>01</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Visual Flow Builder <span style={{ ...T.solTag, background: `rgba(${accentR},0.1)`, color: accent }}>Core Product</span></h4>
              <p style={T.solBody}>A drag-and-drop canvas that lets users build chatbot conversation flows visually, with pre-built blocks for lead capture, FAQs, and booking — no coding required.</p>
              <div style={{ ...T.solResult, color: accent, background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)` }}>The Result: Average bot creation time dropped from 3 days to 28 minutes</div>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>02</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Intelligent Template Library</h4>
              <p style={T.solBody}>Industry-specific templates (e-commerce, restaurants, services) with smart suggestions based on business type, reducing blank-canvas anxiety for new users.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>03</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Unified Inbox</h4>
              <p style={T.solBody}>A single dashboard merging live chat, bot conversations, and lead management so owners never miss a customer interaction across any channel.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>04</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>Real-Time Analytics Panel</h4>
              <p style={T.solBody}>Plain-language performance metrics showing bot effectiveness, drop-off points, and top-performing flows — designed for non-technical business owners.</p>
            </div>
          </div>
          <div style={T.solCard}>
            <div style={{ ...T.solNum, color: accent }}>05</div>
            <div style={{ flex: 1 }}>
              <h4 style={T.solTitle}>One-Click Channel Deployment</h4>
              <p style={T.solBody}>Deploy the same bot to WhatsApp, Instagram, and website chat simultaneously with a single toggle — no need to rebuild for each channel.</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Gallery ─────────────────────────────────────────────────── */}
      <Gallery screens={SCREENS} accent={accent} />

      {/* ── Product Strategy ────────────────────────────────────────── */}
      <Accordion label="🎯 The Product Strategy" accent={accent}>
        <div style={{ ...T.section, background: '#032B2F' }} className="cs-section">
          <h3 style={{ ...T.sectionTitle, color: 'white' }}>Solving the SMB Complexity Trap</h3>
          <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.8)' }}>
            The core challenge was not building AI — it was making AI <strong>feel simple</strong> to someone who had never built a chatbot.
          </p>
          <div style={T.strategyGrid} className="cs-strategy-grid">
            <div style={T.strategyBlock}>
              <h4 style={{ ...T.strategyHeading, color: accent }}>The Strategic Pivot</h4>
              <p style={{ ...T.sectionIntro, color: 'rgba(255,255,255,0.75)', marginTop: '0.5rem', marginBottom: 0 }}>
                Early versions overwhelmed SMB users with features. The pivot: <strong>Progressive Empowerment</strong> — start users with a working bot in 5 minutes, then gradually unlock advanced features as confidence grows.
              </p>
            </div>
            <div style={T.strategyBlock}>
              <h4 style={{ ...T.strategyHeading, color: accent }}>The Strategic Trade-off</h4>
              <div style={T.tradeoffItem}>
                <span style={{ ...T.tradeoffLabel, color: accent }}>The Tension</span>
                <span style={T.tradeoffText}>Power users wanted full customization; new users needed guardrails and guidance.</span>
              </div>
              <div style={{ ...T.tradeoffItem, marginTop: '0.9rem' }}>
                <span style={{ ...T.tradeoffLabel, color: accent }}>The Decision</span>
                <span style={T.tradeoffText}>A tiered experience — a guided Quick Start path for beginners and an advanced mode unlocked after first successful bot deployment.</span>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Journey ─────────────────────────────────────────────────── */}
      <Accordion label="🔍 The Journey" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>How We Got There</h3>
          <div style={T.journeyGrid} className="cs-journey-grid">

            {/* Challenge card */}
            <div style={T.journeyCard}>
              <div style={T.journeyIcon}><FlatIcon name="warning" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Challenge &amp; Pain Points</h4>
              <p style={T.sectionIntro}>SMB owners needed to compete with enterprise customer engagement but had zero technical resources. Existing tools were either too complex or too limited.</p>
              {[
                { q: '"I tried three chatbot tools and gave up every time. I run a restaurant, not a software company."', who: '— Restaurant Owner, SMB User' },
                { q: '"I lose leads every night when I am closed. I need something that responds for me."',              who: '— E-commerce Store Owner' },
                { q: '"The setup took 2 days and I still do not know if it is working."',                               who: '— Service Business Owner' },
              ].map((q) => (
                <div key={q.who} style={{ ...T.quote, borderLeft: '3px solid #00eb9c' }}>
                  <p style={T.quoteText}>{q.q}</p>
                  <cite style={{ ...T.quoteCite, color: accent }}>{q.who}</cite>
                </div>
              ))}
            </div>

            {/* Aha Moment card */}
            <div style={{ ...T.journeyCard, background: 'rgba(0,235,156,0.04)', border: '1px solid rgba(0,235,156,0.15)' }}>
              <div style={T.journeyIcon}><FlatIcon name="lightbulb" size={24} color="currentColor" /></div>
              <h4 style={T.journeyTitle}>The Aha Moment</h4>
              <p style={T.sectionIntro}>Through <strong>24 interviews with SMB owners</strong> and usability testing, I discovered the core blocker: <strong>Fear of Failure.</strong></p>
              <p style={{ ...T.sectionIntro, marginTop: '0.75rem' }}>
                Users were not afraid of technology — they were afraid of wasting time on something that would not work. This insight led to the <strong>5-Minute Win</strong> onboarding strategy: get users a live, working bot before asking them to invest further.
              </p>
            </div>
          </div>

          {/* Research Methods */}
          <h4 style={T.subsectionTitle}>Research Methods</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'mic', title: 'SMB Owner Interviews',      desc: '24 interviews across 6 business categories' },
              { icon: 'device_mobile', title: 'Competitor Teardowns',       desc: 'Analysed 8 chatbot platforms for friction points' },
              { icon: 'bar_chart', title: 'Activation Funnel Analysis', desc: 'Mapped 1,200 drop-off points in the onboarding flow' },
              { icon: 'science', title: 'Usability Testing',          desc: '5 rounds of testing with non-technical participants' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Key Insights */}
          <h4 style={T.subsectionTitle}>Key Insights</h4>
          <div style={T.researchGrid} className="cs-research-grid">
            {[
              { icon: 'sentiment', title: 'Fear of Blank Canvas',    desc: "Users froze when faced with an empty flow builder — they needed a starting point, not a blank slate." },
              { icon: 'speed',  title: 'Time is the Real Cost',   desc: 'SMB owners valued time over features. A fast, simple setup beat a powerful but complex one every time.' },
              { icon: 'trending_up', title: 'Proof Before Investment', desc: 'Users needed to see results — even small ones — before they would commit to learning the platform.' },
              { icon: 'hub', title: 'Iteration is the Product', desc: 'The most successful users were those who could quickly test, fail, and improve their bots without fear.' },
            ].map((r) => (
              <div key={r.title} style={T.researchItem}>
                <FlatIcon name={r.icon} size={20} color="currentColor" style={T.researchIcon} />
                <div>
                  <strong style={T.researchTitle}>{r.title}</strong>
                  <p style={T.researchDesc}>{r.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Testing & Iteration */}
          <h4 style={T.subsectionTitle}>Testing &amp; Iteration — 3 Onboarding Redesigns</h4>
          <div style={T.iterTimeline} className="cs-iter-timeline">
            <div style={{ ...T.iterItem, background: 'rgba(239,68,68,0.06)', borderColor: 'rgba(239,68,68,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#ef4444' }} />
              <div>
                <strong style={T.iterLabel}>Version 1</strong>
                <p style={T.iterText}>Full feature tour upfront — <span style={{ color: '#ef4444', fontWeight: 600 }}>22% activation rate</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(251,146,60,0.06)', borderColor: 'rgba(251,146,60,0.2)' }}>
              <span style={{ ...T.iterDot, background: '#f97316' }} />
              <div>
                <strong style={T.iterLabel}>Version 2</strong>
                <p style={T.iterText}>Simplified 3-step wizard — <span style={{ color: '#f97316', fontWeight: 600 }}>51% activation rate</span></p>
              </div>
            </div>
            <span style={T.iterArrow}>→</span>
            <div style={{ ...T.iterItem, background: 'rgba(0,235,156,0.07)', borderColor: 'rgba(0,235,156,0.25)' }}>
              <span style={{ ...T.iterDot, background: '#00eb9c' }} />
              <div>
                <strong style={T.iterLabel}>Version 3 (Final)</strong>
                <p style={T.iterText}>5-Minute Win live bot first — <span style={{ color: '#00eb9c', fontWeight: 600 }}>78% activation rate</span></p>
              </div>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Design Process ──────────────────────────────────────────── */}
      <Accordion label="✏️ Design Process" accent={accent}>
        <div style={{ ...T.section, background: '#fafaf8' }} className="cs-section">
          <h3 style={T.sectionTitle}>The Messy Middle</h3>
          <p style={T.sectionIntro}>Before the 5-Minute Win, there were two failed onboarding strategies, a near-fatal oversimplification, and one product direction that the data forced us to abandon entirely.</p>

          {/* What I Believed — red callout box */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderLeft: `4px solid #00eb9c`, borderRadius: '10px', padding: '1.5rem 1.75rem', marginBottom: '1.5rem', marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#00eb9c', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 600 }}>What I Believed at the Start</p>
            <p style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.45, marginBottom: '0.75rem' }}>
              "If we make it feature-rich and powerful, SMBs will see the value and push through the learning curve."
            </p>
            <p style={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>
              This was the original product direction — a full-featured chatbot builder packed with customisation options. We shipped it. 22% of users completed onboarding. The other 78% left before creating a single flow.
            </p>
          </div>

          {/* 3 Onboarding Strategies — 3-col grid */}
          <h4 style={T.subsectionTitle}>3 Onboarding Strategies — Only One Worked</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '2rem' }} className="cs-three-col">
            {[
              { dot: '#ef4444', border: '#fecaca', label: 'VERSION 1 — FAILED', title: 'Full Feature Tour',          body: 'Every feature shown upfront — chatbot builder, analytics, channels, templates. SMBs felt overwhelmed. Most left before finishing setup. 22% activation rate.' },
              { dot: '#f97316', border: '#fed7aa', label: 'VERSION 2 — FAILED', title: 'Simplified 3-Step Wizard',  body: 'Stripped to three steps. Better — but users still hit a blank canvas at the end and froze. No starting point meant no momentum. 51% activation rate.' },
              { dot: '#22c55e', border: '#bbf7d0', label: 'VERSION 3 — SHIPPED', title: '5-Minute Win',              body: 'Users got a live, working bot on their website before the onboarding ended. The result was tangible before any investment. 78% activation rate.' },
            ].map((v) => (
              <div key={v.label} style={{ background: '#fff', border: `1px solid ${v.border}`, borderRadius: '10px', padding: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: v.dot, display: 'inline-block' }} />
                  <strong style={{ fontSize: '0.78rem', color: v.dot, fontFamily: "'DM Mono', monospace", letterSpacing: '0.04em' }}>{v.label}</strong>
                </div>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.5rem' }}>{v.title}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{v.body}</p>
              </div>
            ))}
          </div>

          {/* Template Library 4-col grid */}
          <h4 style={T.subsectionTitle}>How the Template Library Actually Solved the Blank Canvas Problem</h4>
          <p style={{ ...T.sectionIntro, marginBottom: '1.25rem' }}>The blank canvas was killing activation. We tried 4 approaches before finding what actually worked.</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem', marginBottom: '2rem' }} className="cs-four-col">
            {[
              { v: 'V1', label: 'Empty canvas, tooltips',          result: 'Users froze',      color: '#ef4444', note: 'Tooltips explaining features did not reduce anxiety' },
              { v: 'V2', label: 'Generic starter templates',        result: 'Low relevance',    color: '#f97316', note: 'Generic bots felt foreign to a restaurant or salon owner' },
              { v: 'V3', label: 'Category-based templates',         result: 'Better adoption',  color: '#eab308', note: 'Right idea — but too many categories caused choice paralysis' },
              { v: 'V4', label: 'Auto-suggested by business type',  result: '78% completed',    color: '#22c55e', note: 'One relevant template, pre-selected based on signup data' },
            ].map((e) => (
              <div key={e.v} style={{ background: '#fff', border: `1px solid ${e.color}44`, borderTop: `3px solid ${e.color}`, borderRadius: '8px', padding: '1rem' }}>
                <div style={{ fontSize: '0.68rem', fontFamily: "'DM Mono', monospace", color: e.color, fontWeight: 700, marginBottom: '0.4rem', letterSpacing: '0.06em' }}>{e.v}</div>
                <p style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1A1A1A', fontFamily: "'DM Sans', sans-serif", marginBottom: '0.35rem', lineHeight: 1.35 }}>{e.label}</p>
                <p style={{ fontSize: '0.92rem', fontWeight: 700, color: e.color, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.4rem' }}>{e.result}</p>
                <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", margin: 0 }}>{e.note}</p>
              </div>
            ))}
          </div>

          {/* The Decision That Almost Did Not Happen — dark bg */}
          <div style={{ background: '#032B2F', borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.75rem', fontWeight: 600 }}>The Decision That Almost Did Not Happen</p>
            <p style={{ fontSize: '1.02rem', fontWeight: 700, color: '#fff', fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5, marginBottom: '1rem' }}>
              The engineering team pushed back on the auto-suggestion feature. They argued it required business-type classification at signup, which added complexity to the registration flow.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              Their concern was legitimate — adding a field to signup risked increasing drop-off before the user even reached onboarding. But removing it meant returning to the blank canvas problem that had killed two previous versions.
            </p>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.8, fontFamily: "'DM Sans', sans-serif", marginBottom: '0.85rem' }}>
              The solution: move business-type selection into the onboarding flow itself, framing it as personalisation rather than a form field. One question, visually styled as cards — not a dropdown. Drop-off did not increase. Template relevance did.
            </p>
            <div style={{ background: `rgba(${accentR},0.08)`, border: `1px solid rgba(${accentR},0.2)`, borderRadius: '8px', padding: '1rem 1.25rem' }}>
              <p style={{ fontSize: '0.88rem', color: accent, lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                This framing shift — from data collection to personalisation — became a design principle applied to every subsequent onboarding step.
              </p>
            </div>
          </div>

          {/* Documented Design Principle */}
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '1.4rem', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}><FlatIcon name="rule" size={22} color="#94a3b8" /></span>
            <div>
              <p style={{ fontSize: '0.72rem', fontFamily: "'DM Mono', monospace", color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.4rem', fontWeight: 600 }}>DOCUMENTED DESIGN PRINCIPLE</p>
              <p style={{ fontSize: '0.95rem', color: '#1A1A1A', lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", margin: 0, fontStyle: 'italic' }}>
                "Every question we ask a user must feel like it benefits them, not us. If it feels like a form, redesign it."
              </p>
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif", margin: '0.5rem 0 0' }}>
                Applied to every data-collection moment across the entire platform.
              </p>
            </div>
          </div>
        </div>
      </Accordion>

      {/* ── Business Impact ─────────────────────────────────────────── */}
      <Accordion label="📈 Business Impact" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>Measurable Results</h3>
          <div style={T.impactGroups}>
            {IMPACT_GROUPS.map((group) => (
              <div key={group.groupLabel} style={T.impactGroup}>
                <div style={{...T.impactGroupLabel, display:"flex", alignItems:"center", gap:"0.5rem"}}><FlatIcon name={group.groupIcon || "bolt"} size={14} color="currentColor" />{group.groupLabel}</div>
                <div style={T.impactStats} className="cs-impact-stats">
                  {group.stats.map((s) => (
                    <div key={s.desc} style={T.impactStat}>
                      <span style={{ ...T.impactNum, color: accent }}>{s.num}</span>
                      <span style={T.impactDesc}>
                        {s.desc}
                        {s.sub && <small style={{ display: 'block', fontSize: '0.68rem', color: '#94a3b8', marginTop: '2px' }}>{s.sub}</small>}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── User Feedback ───────────────────────────────────────────── */}
      <Accordion label="💬 User Feedback" accent={accent}>
        <div style={T.section} className="cs-section">
          <h3 style={T.sectionTitle}>In Their Own Words</h3>
          <div style={T.quotesGrid} className="cs-quotes-grid">
            {[
              'I had a working chatbot on my website in 20 minutes. I did not believe it was possible until I did it.',
              'We stopped losing leads overnight. The bot handles enquiries while I sleep — it paid for itself in week one.',
              'I tried two other tools before this. Weconnect is the first one I actually understood.',
            ].map((q, i) => (
              <div key={i} style={T.userQuote}>{q}</div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* ── Key Learnings ───────────────────────────────────────────── */}
      <Accordion label="💡 Key Learnings &amp; Reflection" accent={accent}>
        <div style={{ ...T.section, background: '#f8fafc' }} className="cs-section">
          <h3 style={T.sectionTitle}>What We Learned</h3>
          <div style={T.learningsGrid} className="cs-learnings-grid">
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What Worked</h4>
              <ul style={T.caseList}>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}>
                  <FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />
                  The <strong>5-Minute Win</strong> strategy — getting users a live result before asking for investment was the single biggest lever for activation
                </li>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />Template-first approach eliminated blank-canvas anxiety and got users moving immediately</li>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="check_circle" size={14} color={accent} style={{flexShrink:0,marginTop:"5px"}} />Data-driven iteration — rebuilding onboarding 3 times based on real drop-off data was the right call</li>
              </ul>
            </div>
            <div style={T.learningsCol}>
              <h4 style={{ ...T.learningsColTitle, borderColor: accent }}>What I Would Change</h4>
              <ul style={T.caseList}>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />Involve SMB users in week 1 of design, not just testing — earlier insight would have saved the first two failed onboarding versions</li>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />Build the analytics panel earlier — users who could see their bot working stayed longer</li>
                <li style={{...T.caseListItem, display:"flex", gap:"0.5rem", alignItems:"flex-start", paddingLeft:0, position:"static"}}><FlatIcon name="trending_up" size={14} color="#94a3b8" style={{flexShrink:0,marginTop:"5px"}} />Design for mobile-first from the start — 60% of SMB owners managed their business from phones</li>
              </ul>
            </div>
          </div>

          {/* Core Takeaway */}
          <div style={T.takeaway}>
            <FlatIcon name="star" size={24} color={accent} style={T.takeawayIcon} />
            <div>
              <strong style={T.takeawayTitle}>Core Takeaway</strong>
              <p style={T.takeawayText}>The best onboarding does not teach the product — it delivers the product core value before the user has time to doubt themselves. For SMBs, a single working result is worth more than ten feature explanations.</p>
            </div>
          </div>
        </div>
      </Accordion>

    </div>
  );
}
